import type { PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import {
  DOC_PERM,
  docInclude,
  documentView,
  kindOf,
  loadDocument,
  logEvent,
  type DocKind,
} from './documents.service.js';
import { renderDocumentPdf } from './pdf.service.js';
import {
  DEFAULT_REMINDER_SETTINGS,
  cancelDocumentReminders,
  getReminderSettings,
  scheduleDocumentReminders,
  snoozeDocumentReminders,
} from './reminders.service.js';
import { assertCanSend, sendDocument, sendInputSchema, sendPreview } from './send.service.js';
import { createShareLink, shareUrl } from './share.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

export function createSalesSendRouter({ db, config }: Deps) {
  const router = Router();
  // Scoped to the paths of this router so it never intercepts routes that belong to other routers.
  router.use(
    ['/sales/documents', '/sales/receipts', '/sales/reminders'],
    authenticate({ db, config }),
    idempotent(db),
  );

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });
  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);

  async function receiptFor(req: Request) {
    if (!can(req, 'sales.receipt.view'))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    const r = await db.paymentReceipt.findFirst({
      where: {
        id: Number(req.params.id),
        companyId: req.auth!.companyId,
        ...(req.auth!.scope === 'own' ? { createdBy: req.auth!.userId } : {}),
      },
    });
    if (!r) throw new HttpError(404, 'NOT_FOUND', 'Receipt not found');
    return r;
  }
  async function docFor(req: Request, action = 'view') {
    const doc = await loadDocument(db, req.auth!, Number(req.params.id));
    if (!can(req, `${DOC_PERM[kindOf(doc)]}.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    return doc;
  }

  const sendPdf = (
    req: Request,
    res: import('express').Response,
    pdf: Buffer,
    filename: string,
  ) => {
    res.setHeader('Content-Type', 'application/pdf');
    res.setHeader('X-Content-Type-Options', 'nosniff');
    res.setHeader(
      'Content-Disposition',
      `${req.query.inline === '1' ? 'inline' : 'attachment'}; filename="${filename.replace(/"/g, '')}"`,
    );
    res.send(pdf);
  };

  // ───── PDF ─────
  router.get(
    '/sales/documents/:id/pdf',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req);
      const { pdf, filename } = await renderDocumentPdf(db, doc.companyId, kindOf(doc), doc.id);
      sendPdf(req, res, pdf, filename);
    }),
  );
  router.get(
    '/sales/receipts/:id/pdf',
    asyncHandler(async (req, res) => {
      const r = await receiptFor(req);
      const { pdf, filename } = await renderDocumentPdf(db, r.companyId, 'receipt', r.id);
      sendPdf(req, res, pdf, filename);
    }),
  );

  // ───── share link (for "copy link") ─────
  router.post(
    '/sales/documents/:id/share-link',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'send');
      if (['draft', 'pending_approval'].includes(doc.status))
        throw new HttpError(409, 'NEEDS_APPROVAL', 'Approve the document before sharing it');
      const { token, expiresAt } = await createShareLink(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: doc.id,
        userId: req.auth!.userId,
      });
      await audit(db, actor(req), {
        action: 'share_link.created',
        entity: 'sales_document',
        entityId: doc.id,
      });
      res.status(201).json({ url: shareUrl(config.APP_URL, token), expiresAt });
    }),
  );
  router.post(
    '/sales/receipts/:id/share-link',
    asyncHandler(async (req, res) => {
      const r = await receiptFor(req);
      assertCanSend(req.auth!, 'receipt');
      const { token, expiresAt } = await createShareLink(db, {
        companyId: r.companyId,
        entityType: 'receipt',
        entityId: r.id,
        userId: req.auth!.userId,
      });
      res.status(201).json({ url: shareUrl(config.APP_URL, token), expiresAt });
    }),
  );

  // ───── send dialog ─────
  router.get(
    '/sales/documents/:id/send-preview',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'send');
      res.json(await sendPreview(db, req.auth!, kindOf(doc), doc.id));
    }),
  );
  router.get(
    '/sales/receipts/:id/send-preview',
    asyncHandler(async (req, res) => {
      const r = await receiptFor(req);
      assertCanSend(req.auth!, 'receipt');
      res.json(await sendPreview(db, req.auth!, 'receipt', r.id));
    }),
  );
  router.post(
    '/sales/documents/:id/send',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'send');
      const body = parse(sendInputSchema, req.body);
      const result = await sendDocument(db, config, req.auth!, kindOf(doc), doc.id, body);
      await audit(db, actor(req), {
        action: `${kindOf(doc)}.sent`,
        entity: 'sales_document',
        entityId: doc.id,
        after: { channels: body.channels, recipients: result.communications.length },
      });
      res.status(201).json(result);
    }),
  );
  router.post(
    '/sales/receipts/:id/send',
    asyncHandler(async (req, res) => {
      const r = await receiptFor(req);
      const body = parse(sendInputSchema, req.body);
      const result = await sendDocument(db, config, req.auth!, 'receipt', r.id, body);
      await audit(db, actor(req), {
        action: 'receipt.sent',
        entity: 'payment_receipt',
        entityId: r.id,
        after: { channels: body.channels },
      });
      res.status(201).json(result);
    }),
  );

  // ───── bulk send / reminder from a list ─────
  router.post(
    '/sales/documents/bulk-send',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          ids: z.array(z.number().int().positive()).min(1).max(50),
          action: z.enum(['send', 'reminder']),
        }),
        req.body,
      );
      const results: { id: number; number: string | null; ok: boolean; message: string }[] = [];
      for (const id of body.ids) {
        try {
          const doc = await docFor(
            { ...req, params: { id: String(id) } } as unknown as Request,
            'send',
          );
          const kind = kindOf(doc);
          const preview = await sendPreview(db, req.auth!, kind, id);
          if (preview.defaultEmails.length === 0)
            throw new HttpError(
              422,
              'NO_RECIPIENTS',
              'No contact with an email address is set to receive documents',
            );
          let subject = preview.email.subject;
          let message = preview.email.body;
          if (body.action === 'reminder') {
            const tpl = await db.messageTemplate.findFirst({
              where: {
                companyId: doc.companyId,
                docType: kind,
                channel: 'email',
                name: 'reminder',
              },
            });
            if (tpl) {
              const { renderTemplate } = await import('../messaging/messaging.service.js');
              const vars = {
                client_name: preview.partyName,
                document_number: doc.number ?? '',
                total: '',
                due_date: '',
                valid_until: '',
                link: '{{link}}',
                company_name: '',
                sender_name: req.auth!.name,
              };
              subject = renderTemplate(tpl.subject ?? subject, vars);
              message = renderTemplate(tpl.body, vars);
            }
          }
          const r = await sendDocument(db, config, req.auth!, kind, id, {
            channels: ['email'],
            includeLink: true,
            email: {
              to: preview.defaultEmails,
              cc: [],
              bcc: [],
              subject,
              message,
              attachPdf: true,
            },
          });
          results.push({
            id,
            number: r.number,
            ok: true,
            message: `Sent to ${r.communications.length} recipient(s)`,
          });
        } catch (e) {
          results.push({
            id,
            number: null,
            ok: false,
            message: e instanceof Error ? e.message : 'Failed',
          });
        }
      }
      res.json({
        results,
        sent: results.filter((r) => r.ok).length,
        failed: results.filter((r) => !r.ok).length,
      });
    }),
  );

  // ───── reminder controls on one document ─────
  router.get(
    '/sales/documents/:id/reminders',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req);
      const rows = await db.scheduledReminder.findMany({
        where: { docId: doc.id },
        orderBy: { runAt: 'asc' },
      });
      const rules = await db.reminderRule.findMany({
        where: { id: { in: rows.map((r) => r.ruleId) } },
      });
      res.json({
        stopped: doc.remindersStopped,
        items: rows.map((r) => ({
          id: r.id,
          rule: rules.find((x) => x.id === r.ruleId)?.name ?? '',
          runAt: r.runAt,
          status: r.status,
          note: r.note,
        })),
      });
    }),
  );
  router.post(
    '/sales/documents/:id/reminders/snooze',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'edit');
      const { days } = parse(z.object({ days: z.number().int().min(1).max(60) }), req.body);
      const moved = await snoozeDocumentReminders(db, doc.docType, doc.id, days);
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: doc.id,
        type: 'reminders_snoozed',
        userId: req.auth!.userId,
        note: `${days} days`,
      });
      res.json({ moved });
    }),
  );
  router.post(
    '/sales/documents/:id/reminders/stop',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'edit');
      await db.salesDocument.update({ where: { id: doc.id }, data: { remindersStopped: true } });
      await cancelDocumentReminders(db, doc.docType, doc.id);
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: doc.id,
        type: 'reminders_stopped',
        userId: req.auth!.userId,
      });
      res.json(
        documentView(
          await db.salesDocument.findUniqueOrThrow({ where: { id: doc.id }, include: docInclude }),
        ),
      );
    }),
  );
  router.post(
    '/sales/documents/:id/reminders/resume',
    asyncHandler(async (req, res) => {
      const doc = await docFor(req, 'edit');
      const updated = await db.salesDocument.update({
        where: { id: doc.id },
        data: { remindersStopped: false },
      });
      await scheduleDocumentReminders(db, updated);
      await logEvent(db, {
        companyId: doc.companyId,
        entityType: 'sales_document',
        entityId: doc.id,
        type: 'reminders_resumed',
        userId: req.auth!.userId,
      });
      res.json({ ok: true });
    }),
  );

  // ───── reminder rules and settings (Settings > Reminder Rules) ─────
  const needSetting = (req: Request, action: 'view' | 'edit') => {
    if (!can(req, `settings.reminder.${action}`))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  router.get(
    '/sales/reminders/config',
    asyncHandler(async (req, res) => {
      needSetting(req, 'view');
      const rules = await db.reminderRule.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: [{ docType: 'asc' }, { trigger: 'asc' }, { offsetDays: 'asc' }],
      });
      const autoSend = await db.setting.findUnique({
        where: { companyId_key: { companyId: req.auth!.companyId, key: 'autoSend' } },
      });
      res.json({
        settings: await getReminderSettings(db, req.auth!.companyId),
        autoSend: {
          invoice: false,
          receipt: false,
          ...((autoSend?.value as object | undefined) ?? {}),
        },
        rules,
      });
    }),
  );
  router.put(
    '/sales/reminders/config',
    asyncHandler(async (req, res) => {
      needSetting(req, 'edit');
      const body = parse(
        z.object({
          settings: z
            .object({
              clientMode: z.enum(['off', 'ask', 'auto']),
              windowStartHour: z.number().int().min(0).max(23),
              windowEndHour: z.number().int().min(1).max(24),
              maxClientReminders: z.number().int().min(0).max(10),
            })
            .refine(
              (s) => s.windowEndHour > s.windowStartHour,
              'The send window must end after it starts',
            ),
          autoSend: z.object({ invoice: z.boolean(), receipt: z.boolean() }),
        }),
        req.body,
      );
      const companyId = req.auth!.companyId;
      await db.setting.upsert({
        where: { companyId_key: { companyId, key: 'reminders' } },
        create: { companyId, key: 'reminders', value: body.settings },
        update: { value: body.settings },
      });
      await db.setting.upsert({
        where: { companyId_key: { companyId, key: 'autoSend' } },
        create: { companyId, key: 'autoSend', value: body.autoSend },
        update: { value: body.autoSend },
      });
      await audit(db, actor(req), {
        action: 'settings.reminders_updated',
        entity: 'settings',
        after: body,
      });
      res.json({ ok: true, defaults: DEFAULT_REMINDER_SETTINGS });
    }),
  );
  const ruleSchema = z.object({
    docType: z.enum(['quotation', 'invoice']),
    name: z.string().trim().min(3).max(100),
    trigger: z.enum(['after_sent', 'before_due', 'after_due', 'before_expiry']),
    offsetDays: z.number().int().min(0).max(180),
    audience: z.enum(['owner', 'client', 'both']),
    templateName: z.string().trim().min(1).max(100).default('reminder'),
    active: z.boolean().default(true),
  });
  router.post(
    '/sales/reminders/rules',
    asyncHandler(async (req, res) => {
      needSetting(req, 'edit');
      const body = parse(ruleSchema, req.body);
      const rule = await db.reminderRule.create({
        data: { companyId: req.auth!.companyId, ...body },
      });
      await audit(db, actor(req), {
        action: 'reminder_rule.created',
        entity: 'reminder_rule',
        entityId: rule.id,
        after: body,
      });
      res.status(201).json(rule);
    }),
  );
  router.patch(
    '/sales/reminders/rules/:id',
    asyncHandler(async (req, res) => {
      needSetting(req, 'edit');
      const id = Number(req.params.id);
      const before = await db.reminderRule.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Rule not found');
      const body = parse(ruleSchema.partial(), req.body);
      const after = await db.reminderRule.update({ where: { id }, data: body });
      await audit(db, actor(req), {
        action: 'reminder_rule.updated',
        entity: 'reminder_rule',
        entityId: id,
        before,
        after,
      });
      res.json(after);
    }),
  );
  router.delete(
    '/sales/reminders/rules/:id',
    asyncHandler(async (req, res) => {
      needSetting(req, 'edit');
      const id = Number(req.params.id);
      const before = await db.reminderRule.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Rule not found');
      await db.reminderRule.update({ where: { id }, data: { active: false } });
      await audit(db, actor(req), {
        action: 'reminder_rule.disabled',
        entity: 'reminder_rule',
        entityId: id,
        before,
      });
      res.status(204).end();
    }),
  );

  void (undefined as unknown as DocKind);
  return router;
}
